import React, { useState, useRef, useCallback } from 'react';
import { cn } from '../utils/cn';

export interface DotSliderProps {
    value: number;
    onValueChange: (value: number) => void;
    min?: number;
    max?: number;
    reversed?: boolean;
    className?: string;
    disabled?: boolean;
}

export const DotSlider = React.forwardRef<HTMLDivElement, DotSliderProps>(
    ({ value, onValueChange, min = 1, max = 10, reversed = false, className, disabled = false }, ref) => {
        const [hoverValue, setHoverValue] = useState<number | null>(null);
        const [isDragging, setIsDragging] = useState(false);
        const internalRef = useRef<HTMLDivElement>(null);

        // merge refs
        const setRefs = useCallback((node: HTMLDivElement | null) => {
            // set internal ref
            (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            // forward to external ref
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
        }, [ref]);

        const handleClick = (val: number) => {
            if (disabled) return;
            const rating = reversed ? max + min - val : val;
            onValueChange(rating);
        };

        // calculate which dot is under a given x position
        const getDotFromPosition = useCallback((clientX: number): number | null => {
            if (!internalRef.current) return null;
            const rect = internalRef.current.getBoundingClientRect();
            const x = clientX - rect.left;
            const width = rect.width;
            const dotCount = max - min + 1;
            const dotIndex = Math.floor((x / width) * dotCount);
            const clampedIndex = Math.max(0, Math.min(dotCount - 1, dotIndex));
            return min + clampedIndex;
        }, [min, max]);

        const handleDragStart = (clientX: number) => {
            if (disabled) return;
            setIsDragging(true);
            const dotValue = getDotFromPosition(clientX);
            if (dotValue !== null) {
                handleClick(dotValue);
            }
        };

        const handleDragMove = (clientX: number) => {
            if (disabled || !isDragging) return;
            const dotValue = getDotFromPosition(clientX);
            if (dotValue !== null) {
                handleClick(dotValue);
            }
        };

        const handleDragEnd = () => {
            setIsDragging(false);
        };

        // mouse events
        const handleMouseDown = (e: React.MouseEvent) => {
            handleDragStart(e.clientX);
        };

        const handleMouseMove = (e: React.MouseEvent) => {
            handleDragMove(e.clientX);
        };

        const handleMouseUp = () => {
            handleDragEnd();
        };

        const handleMouseLeave = () => {
            setHoverValue(null);
            handleDragEnd();
        };

        // touch events
        const handleTouchStart = (e: React.TouchEvent) => {
            if (e.touches.length === 1) {
                handleDragStart(e.touches[0].clientX);
            }
        };

        const handleTouchMove = (e: React.TouchEvent) => {
            if (e.touches.length === 1) {
                handleDragMove(e.touches[0].clientX);
            }
        };

        const handleTouchEnd = () => {
            handleDragEnd();
        };

        // color based on how far along the bar the dot is
        const getFilledColor = (dotIndex: number, totalDots: number) => {
            const ratio = dotIndex / totalDots;
            if (ratio >= 0.8) return 'bg-emerald-400';
            if (ratio >= 0.5) return 'bg-mint';
            if (ratio >= 0.3) return 'bg-amber-300';
            return 'bg-orange-300';
        };

        const dots = [];
        const totalDots = max - min + 1;
        for (let i = min; i <= max; i++) {
            const displayValue = hoverValue ?? value;
            const filled = value >= min && i <= displayValue;
            dots.push(
                <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    className={cn(
                        "flex-1 h-6 rounded-[3px] cursor-pointer transition-all duration-150",
                        disabled && "cursor-not-allowed opacity-50",
                        filled
                            ? getFilledColor(i - min, totalDots)
                            : "bg-gray-200"
                    )}
                    onMouseEnter={() => !disabled && setHoverValue(i)}
                    onMouseLeave={() => setHoverValue(null)}
                    onClick={() => handleClick(i)}
                />
            );
        }

        return (
            <div
                ref={setRefs}
                className={cn(
                    "flex w-full gap-1 items-center touch-manipulation select-none",
                    className
                )}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {dots}
            </div>
        );
    }
);
DotSlider.displayName = 'DotSlider';
